import { usePageTitle } from "../hooks/usePageTitle";
import { Zap, Target, Layers, ArrowRight } from "lucide-react";
import { FadeUp, CardHover } from "../hooks/motion";
import { CALCOM_DEMO_URL } from "../config/demo";

export default function AboutPage() {
  usePageTitle("About | CleanBid.ai");
  return (
    <div className="bg-[#0A0A0F] pt-20">
      {/* Hero */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          {/* Crest — CleanBid symbol, centered */}
          <div className="flex justify-center" style={{ marginBottom: "-16px" }}>
            <div className="relative flex items-center justify-center">
              {/* Aqua radial glow behind the logo */}
              <div
                className="absolute pointer-events-none"
                style={{
                  width: "180px",
                  height: "180px",
                  background: "radial-gradient(circle, rgba(113,222,214,0.15) 0%, transparent 70%)",
                  zIndex: 0,
                }}
              />
              {/* CleanBid symbol — transparent PNG, no background panel */}
              <img
                src="/cleanbid-logo-transparent.png"
                alt=""
                aria-hidden="true"
                className="pointer-events-none select-none"
                style={{ width: "120px", height: "auto", opacity: 0.95, position: "relative", zIndex: 1 }}
              />
            </div>
          </div>
          <FadeUp>
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">About</span>
          </FadeUp>
          <FadeUp delay={60}>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-[#F4F4F8] mt-4 mb-8 max-w-3xl">
              We built this because we've been inside these businesses.
            </h1>
          </FadeUp>
          <FadeUp delay={120}>
            <div className="space-y-5 text-lg text-[#8B8BA7] leading-relaxed max-w-2xl">
              <p>
                We spent years working alongside owner-operators in cleaning, pest control, lawn care, and exterior services. Watching them lose jobs not because their service was bad — but because their tools weren't built for how they actually work.
              </p>
              <p>
                They were running their business off a personal cell phone. Quotes lived in their head. Follow-ups happened whenever they remembered. Reviews almost never got asked for.
              </p>
              <p>
                The tools that existed were built for larger companies with office staff, not for someone doing installs at 7am and sending invoices from their truck at 6pm. So we built CleanBid.
              </p>
              <p>
                Not a project management tool with a CRM bolted on. A revenue system — built from the ground up to help one-to-eight-person home service teams close faster, follow up automatically, and collect reviews without asking.
              </p>
              <p className="text-[#F4F4F8] font-medium">
                If you're running a service business and you're still chasing leads manually, CleanBid is for you.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 border-t border-[#1E1E2E]" data-testid="values-section">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <FadeUp className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F4F4F8]">
              How we build.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <Zap className="w-7 h-7 text-[#71DED6]" />,
                title: "Speed wins",
                body: "In home services, the first business to respond usually wins the job. Everything we build is optimized for speed — from quote delivery to follow-up timing to review requests.",
                testid: "value-speed",
              },
              {
                icon: <Target className="w-7 h-7 text-[#6B5CF6]" />,
                title: "Outcomes over features",
                body: "We don't build features to check boxes. We ask one question: does this help our customers close more jobs or collect more reviews? If the answer is no, we don't build it.",
                testid: "value-outcomes",
              },
              {
                icon: <Layers className="w-7 h-7 text-[#71DED6]" />,
                title: "Simple by design",
                body: "Owner-operators don't have time for complex software. CleanBid should be learnable in an afternoon and useful the same day. Every feature is as simple as we can make it without losing power.",
                testid: "value-simple",
              },
            ].map((val, i) => (
              <FadeUp key={i} delay={i * 80}>
                <CardHover>
                <div
                  data-testid={val.testid}
                  className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-8 transition-[border-color] duration-300 hover:border-[#71DED6]/30"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#0A0A0F] border border-[#1E1E2E] flex items-center justify-center mb-5">
                    {val.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#F4F4F8] mb-3">{val.title}</h3>
                  <p className="text-[#8B8BA7] text-sm leading-relaxed">{val.body}</p>
                </div>
                </CardHover>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-[#111118]/40 border-t border-[#1E1E2E]" data-testid="about-stats">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { val: "200+", label: "Service businesses" },
              { val: "50K+", label: "Quotes sent" },
              { val: "4.2x", label: "More reviews" },
              { val: "47%", label: "More closed jobs" },
            ].map((stat, i) => (
              <FadeUp key={i} delay={i * 60}>
                <div>
                  <p className="text-3xl md:text-4xl font-bold text-[#F4F4F8]">{stat.val}</p>
                  <p className="text-sm text-[#8B8BA7] mt-1">{stat.label}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 glow-cta" data-testid="about-cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <FadeUp>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F4F4F8] mb-4">
              Ready to build your revenue system?
            </h2>
          </FadeUp>
          <FadeUp delay={80}>
            <p className="text-[#8B8BA7] mb-8 text-lg">
              Book a 20-minute demo and see how CleanBid works for your business.
            </p>
          </FadeUp>
          <FadeUp delay={160}>
            <a
              href={CALCOM_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-cta-button"
              className="bg-[#71DED6] text-[#10151D] font-semibold px-10 py-4 rounded-full text-lg hover:scale-[1.02] hover:shadow-glow-green transition-transform duration-200 inline-flex items-center gap-2"
            >
              Book a Demo <ArrowRight className="w-5 h-5" />
            </a>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
